import React from 'react';
import { Box, Divider, Grid, Link, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const Footer: React.FC = () => {
  const { token } = useAuth();
  const year = new Date().getFullYear();
  const ridesPath = token ? '/app/rides' : '/rides';

  const cols = [
    {
      heading: 'Product',
      links: [
        { label: 'How it works', to: '/#how-it-works' },
        { label: 'Why RideSaathi', to: '/#features' },
        { label: 'Live Rides', to: ridesPath },
        { label: 'Pricing', to: '/#pricing' },
        { label: 'Dashboard', to: '/app' },
      ],
    },
    {
      heading: 'Company',
      links: [
        { label: 'About', to: '/about' },
        { label: 'Earnings', to: '/#earnings' },
        { label: 'Testimonials', to: '/#testimonials' },
        { label: 'Contact', to: '/contact' },
        { label: 'FAQ', to: '/faq' },
      ],
    },
    {
      heading: 'Legal',
      links: [
        { label: 'Privacy Policy', to: '/privacy' },
        { label: 'Terms & Conditions', to: '/terms' },
        { label: 'Data Deletion / Opt-out', to: '/data-deletion' },
        { label: 'Refund Policy', to: '/refund-policy' },
        { label: 'Community Policy', to: '/community-policy' },
        { label: 'Contact / Grievance Officer', to: '/grievance-officer' },
      ],
    },
  ];

  return (
    <Box sx={{ bgcolor: '#061b31', color: 'white', pt: { xs: 7, md: 8 }, pb: 4 }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 3, md: 6 } }}>
        <Grid container spacing={6} mb={6}>
          {/* Brand column */}
          <Grid item xs={12} md={4}>
            <Stack spacing={2.5}>
              <Box component="img" src="/assets/ride-saathi-main-logo.png" alt="RideSaathi" sx={{ height: 46, width: 'auto', maxWidth: '100%', display: 'block', alignSelf: 'flex-start', objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
              <Typography variant="body2" color="rgba(226,232,240,0.6)" lineHeight={1.75} maxWidth={280}>
                Driver-focused rides from authorized sources and verified contributors, with route filters and driver credits in one workspace.
              </Typography>
              <Stack direction="row" spacing={1.5} flexWrap="wrap">
                {['Delhi NCR', 'Gurugram', 'Noida', 'Jaipur', 'Chandigarh'].map((city) => (
                  <Typography key={city} variant="caption" sx={{ color: 'rgba(226,232,240,0.45)', fontSize: 11 }}>
                    {city}
                  </Typography>
                ))}
              </Stack>
            </Stack>
          </Grid>

          {/* Link columns */}
          {cols.map((col) => (
            <Grid item xs={6} md={2} key={col.heading}>
              <Stack spacing={2}>
                <Typography variant="caption" fontWeight={700} color="rgba(226,232,240,0.4)" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                  {col.heading}
                </Typography>
                {col.links.map((l) => (
                  <Link
                    key={l.to}
                    component={RouterLink}
                    to={l.to}
                    underline="none"
                    sx={{ color: 'rgba(226,232,240,0.7)', fontSize: 14, '&:hover': { color: '#ffc928' }, transition: 'color 0.15s' }}
                  >
                    {l.label}
                  </Link>
                ))}
              </Stack>
            </Grid>
          ))}

          {/* Contact */}
          <Grid item xs={12} md={2}>
            <Stack spacing={2}>
              <Typography variant="caption" fontWeight={700} color="rgba(226,232,240,0.4)" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                Contact
              </Typography>
              <Typography variant="body2" color="rgba(226,232,240,0.65)" lineHeight={1.7}>
                support@ridesaathi.in
              </Typography>
              <Typography variant="body2" color="rgba(226,232,240,0.65)">
                In-app support for ride, wallet and payment issues
              </Typography>
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mb: 4 }} />

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={1.5}
          flexWrap="wrap"
        >
          <Typography variant="caption" color="rgba(226,232,240,0.4)" sx={{ flex: '1 1 220px', whiteSpace: { xs: 'normal', sm: 'nowrap' } }}>
            © {year} RideSaathi · Chahal Travel. All rights reserved.
          </Typography>
          <Stack direction="row" spacing={2.5} alignItems="center">
            {[{ label: 'Privacy', to: '/privacy' }, { label: 'Terms', to: '/terms' }, { label: 'Data Requests', to: '/data-deletion' }].map((l) => (
              <Link key={l.to} component={RouterLink} to={l.to} underline="none" sx={{ color: 'rgba(226,232,240,0.4)', fontSize: 12, '&:hover': { color: '#ffc928' } }}>
                {l.label}
              </Link>
            ))}
          </Stack>
          <Typography
            variant="caption"
            sx={{
              color: '#9ca3af',
              fontSize: { xs: '10px', sm: '11px' },
              lineHeight: 1.2,
              whiteSpace: { xs: 'normal', sm: 'nowrap' },
              textAlign: 'right',
              ml: { sm: 'auto' },
            }}
          >
            <Link
              href="https://www.sparkwhizinfotech.com"
              target="_blank"
              rel="noopener noreferrer"
              underline="none"
              sx={{ color: 'inherit', '&:hover': { color: 'white' }, transition: 'color 0.15s' }}
            >
              Built with <Box component="span" sx={{ color: '#ef4444' }}>♥</Box> by SparkWhiz Infotech
            </Link>
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};

export default Footer;
